<script lang="ts" setup>
import { validate } from "uuid";
import {
    faPlus,
    faSpinnerThird,
    faTimes,
    faEdit,
} from "@fortawesome/pro-regular-svg-icons";
import { faPause, faPlay, faWifi } from "@fortawesome/pro-solid-svg-icons";

// Breadcrumbs
const breadcrumbs = [
    { label: "Dashboard", to: "/" },
    { label: "Apps", to: { name: "settings-apps" } },
];

// App ID
const appId = useRoute().params.id as string;

// Is Custom App
const isCustomApp = computed(() => validate(appId));

// App Store
const store = useAppStore();

// Loading State
const loading = ref<boolean>(true);

// Creation Type
const creationType = ref<any>(undefined);

// Creation State
const isCreatingConnection = ref<boolean>(false);

// Fetch App
async function fetchApp(showLoading: boolean = true) {
    loading.value = showLoading;
    try {
        await store.find(useRoute().params.id as string);
    } catch (e) {
        // Navigate to apps page on error
        navigateTo({ name: "settings-apps" });
    } finally {
        isCreatingConnection.value = false;
        loading.value = false;
    }
}

onBeforeMount(async () => {
    await fetchApp();
});

// Dayjs
const dayjs = useDayjs();

// Last Updated
const lastUpdated = computed(() =>
    store.app?.updated_at ? dayjs().to(store.app.updated_at) : undefined,
);

// Toggle Status
function toggleStatus() {
    store.update({
        status: store.app?.status === "active" ? "paused" : "active",
    });
}

// Email Region
const emailRegion = computed(() => {
    return (
        (
            store.app as unknown as { info: { label: string; value: string }[] }
        )?.info?.find(
            (item: { label: string; value: string }) =>
                item.label === "Region" && item.value !== "N/A",
        )?.value ?? null
    );
});

// Get Flag Icon
function getFlagIcon(region: string = "") {
    return region === "Europe" ? "eu" : "us";
}

// On Error
function onError(error: Error) {
    console.error(error);
}

function onEdit() {
    navigateTo({
        name: "apps-id-edit",
        params: { id: store.app!.uuid },
    });
}
</script>

<template>
    <AppBuilderPage
        :breadcrumbs="breadcrumbs"
        :loading="loading"
        :title="store.app?.name"
        :editable="isCustomApp"
        label="Apps"
        @edit="onEdit"
    >
        <template #pre-title>
            <div
                v-if="store && store.app"
                class="w-10 h-10 bg-hd-neutral-50 flex items-center relative justify-center rounded-md p-1"
            >
                <UIAvatar
                    size="lg"
                    class="max-w-full max-h-full rounded-md"
                    :src="store.app.icon"
                    :alt="store.app.name"
                />
                <div
                    class="absolute -bottom-0 -right-0 bg-hd-neutral-50 h-5 w-5 flex items-center justify-center rounded-full"
                >
                    <BadgeAppStatus :app="store.app" size="xs" />
                </div>
            </div>
        </template>
        <template v-if="emailRegion" #title-text>
            <div class="flex flex-row items-center gap-2">
                {{ store.app?.name }}
                <UTooltip :text="`Region: ${emailRegion}`">
                    <NuxtImg
                        :src="`/assets/flags/svg/${getFlagIcon(emailRegion)}.svg`"
                        alt="US Icon"
                        class="w-6"
                    />
                </UTooltip>
            </div>
        </template>
        <template v-if="store.app?.is_current_core_app" #title-text>
            <span class="flex items-center"
                >{{ store.app?.name }}
                <UBadge color="hd-neutral" class="ml-2 -mt-2" label="Core App"
            /></span>
        </template>
        <template #actions>
            <div></div>
            <div class="flex gap-4 items-center">
                <div id="action-portal"></div>
                <span class="text-sm">
                    <template v-if="store.saving">
                        Saving your app
                        <FontAwesomeIcon
                            :icon="faSpinnerThird"
                            class="ml-2"
                            spin
                        />
                    </template>
                    <template v-else-if="lastUpdated">
                        Last updated {{ lastUpdated }}
                    </template>
                </span>
                <template
                    v-if="store.app?.is_default && !store.app?.is_installable"
                >
                    <UButton
                        v-if="
                            store.app?.connection_limit === 1
                                ? !store.app?.connections.length
                                : true
                        "
                        class="ml-2 gap-2"
                        color="hd-neutral"
                        size="md"
                        id="app-connect-btn"
                        @click="isCreatingConnection = true"
                    >
                        <FontAwesomeIcon :icon="faPlus" />
                        <span v-if="store.app?.connection_limit === 1"
                            >Setup {{ store?.app?.name }}</span
                        >
                        <span v-else>New Connection</span>
                    </UButton>
                </template>
                <div
                    v-else-if="
                        !(store.app?.is_default && store.app?.is_installable)
                    "
                    class="flex gap-2"
                >
                    <UButton
                        square
                        size="md"
                        color="white"
                        class="h-9 w-9 justify-center"
                        :to="{
                            name: 'apps-id-edit',
                            params: { id: store.app!.uuid },
                        }"
                    >
                        <FontAwesomeIcon :icon="faEdit" fixed-width />
                    </UButton>
                    <UButton
                        :disabled="store.saving"
                        color="primary"
                        size="md"
                        @click="toggleStatus"
                    >
                        <FontAwesomeIcon
                            :icon="
                                store.app!.status === 'active'
                                    ? faPause
                                    : faPlay
                            "
                        />
                        {{
                            $t(
                                "buttons." +
                                    (store.app!.status === "active"
                                        ? "pause"
                                        : "activate"),
                            )
                        }}
                    </UButton>
                </div>
            </div>
        </template>
        <div
            class="flex-1 w-full flex flex-col relative min-h-[0] overflow-y-auto bg-hd-neutral-50 rounded-xl p-8"
        >
            <NuxtErrorBoundary @error="onError">
                <template #error="{ error }">
                    <UAlert
                        color="red"
                        title="Error"
                        :description="error.message"
                    />
                </template>
                <ClientOnly>
                    <PartialCustomAppView v-if="isCustomApp" />
                    <LazyPartialWhatsappView
                        v-else-if="appId === 'whatsapp'"
                        :refresh="fetchApp"
                        v-model:creating="isCreatingConnection"
                    />
                    <LazyPartialFacebookView
                        v-else-if="appId === 'facebook'"
                        :refresh="fetchApp"
                        v-model:creating="isCreatingConnection"
                    />
                    <LazyPartialAppsEmailView
                        v-else-if="appId === 'email'"
                        :refresh="fetchApp"
                        v-model:creating="isCreatingConnection"
                    />
                    <PartialAppView
                        v-else
                        :refresh="fetchApp"
                        v-model:creating="isCreatingConnection"
                    />
                </ClientOnly>
            </NuxtErrorBoundary>
        </div>
        <USlideover
            v-if="store.app?.is_default && store.app?.connection"
            v-model="isCreatingConnection"
        >
            <UCard
                :ui="{
                    background: 'bg-white',
                    rounded: '',
                    body: { base: 'flex-1', padding: '' },
                    ring: '',
                    divide: 'divide-y divide-gray-300',
                }"
                class="flex flex-col flex-1 h-full"
            >
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3
                            class="text-base font-medium font-display leading-6 text-gray-900 dark:text-white"
                        >
                            <FontAwesomeIcon :icon="faWifi" class="mr-2" />
                            <span v-if="store.app?.connection_limit === 1"
                                >Setup {{ store?.app?.name }}</span
                            >
                            <span v-else>New Connection</span>
                        </h3>
                        <UButton
                            color="gray"
                            size="sm"
                            square
                            variant="ghost"
                            @click="isCreatingConnection = false"
                        >
                            <FontAwesomeIcon :icon="faTimes" size="lg" />
                        </UButton>
                    </div>
                </template>
                <div class="p-4">
                    <NuxtErrorBoundary @error="onError">
                        <template #error="{ error }">
                            <UAlert
                                color="red"
                                title="Error"
                                :description="error.message"
                            />
                        </template>
                        <ClientOnly>
                            <LazyPartialWhatsappConnector
                                v-if="appId === 'whatsapp'"
                                @connected="fetchApp"
                                v-model:creation-type="creationType"
                                v-model:creation="isCreatingConnection"
                            />
                            <ThirdPartyConnector
                                v-else
                                :connection="store.app.connection"
                                :uuid="store.app.uuid"
                                :label="store.app.name"
                                :logo="store.app.logo"
                                @connected="fetchApp"
                            />
                        </ClientOnly>
                    </NuxtErrorBoundary>
                </div>
            </UCard>
        </USlideover>
    </AppBuilderPage>
</template>
